import React, { useRef } from 'react';
import { Loading, Modal } from '@brighterion/br-ui';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { EDIT_TENANT_GROUP_FIELDS } from 'Common/enums';
import useUpdateTenantGroup from 'Hooks/query-hooks/tenant-groups/useUpdateTenantGroup';
import useGetTenantGroups from 'Hooks/query-hooks/tenant-groups/useGetTenantGroups';
import { FormikInputField } from 'Components/FormikComponents';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { editTenantGroupSchema } from './schema';
import styles from './EditTenantGroupModal.css';

type TenantGroupIdProps = {
    tenantGroupId: string;
    tenantGroup?: TenantGroup;
    closeModal: boolean;
    setCloseModal: (state: boolean) => void;
};

const TenantGroupId = ({ tenantGroupId, closeModal, setCloseModal, tenantGroup }: TenantGroupIdProps) => {
    const formRef = useRef<FormikProps<PatchTenantGroupPayload>>(null);
    const { t } = useTranslation();
    const mutation = useUpdateTenantGroup(tenantGroupId, formRef, setCloseModal);
    const { data: tenantGroupData } = useGetTenantGroups({});
    const modalInitialValues: PatchTenantGroupPayload = {
        name: tenantGroup?.name ?? '',
        description: tenantGroup?.description ?? ''
    };
    const existingTenantGroupNames: string[] = React.useMemo(() => {
        return tenantGroupData?.original.map((entry) => entry.name!.toLocaleLowerCase()) || [];
    }, [tenantGroupData]);

    return (
        <Modal
            isOpen={!closeModal}
            closeOnClickOutside={false}
            closeModal={() => setCloseModal(true)}
            containerClass={styles.modalContainer}
        >
            <Modal.Title
                id='edit-tenant-group-modal-title'
                title={t('Edit Tenant Group')}
                onClose={() => setCloseModal(true)}
                isSubmitting={mutation.isLoading}
            />
            <Formik
                innerRef={formRef}
                initialValues={modalInitialValues}
                validationSchema={editTenantGroupSchema(t, existingTenantGroupNames, modalInitialValues.name)}
                onSubmit={(formikValues) => {
                    mutation.mutate(formikValues);
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'Tenant Groups - Edit Tenant Group Overview',
                            text: 'Edit Tenant Group Overview'
                        }
                    });
                }}
            >
                {({ dirty, isValid, handleBlur, handleChange, handleSubmit }) => {
                    const disableSave = !dirty || !isValid;

                    return (
                        <div className={styles.formBody}>
                            <FormikInputField
                                name={EDIT_TENANT_GROUP_FIELDS.NAME}
                                label={t('Tenant Group Name')}
                                handleBlur={handleBlur}
                                handleChange={handleChange}
                                disabled={mutation.isLoading}
                            />

                            <FormikInputField
                                name={EDIT_TENANT_GROUP_FIELDS.DESCRIPTION}
                                label={t('Description (Optional)')}
                                className={styles.description}
                                handleBlur={handleBlur}
                                handleChange={handleChange}
                                disabled={mutation.isLoading}
                            />

                            <Modal.Footer
                                id='create-rule-group-modal-footer'
                                onCloseText={t('Cancel')}
                                onActionText={mutation.isLoading ? <Loading size='sm' fillName='white' /> : t('Save')}
                                onAction={handleSubmit}
                                onClose={() => setCloseModal(true)}
                                disableOnCancel={mutation.isLoading}
                                disableOnAction={disableSave || mutation.isLoading}
                            />
                        </div>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default TenantGroupId;
